import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Upload, FileText, Building2, X } from 'lucide-react';
import './LMCURefiUpload.css';

const AI_PROVIDERS = [
  { id: 'claude', name: 'Claude' },
  { id: 'openai', name: 'GPT' },
  { id: 'gemini', name: 'Gemini' }
];

function LMCURefiUpload({ onFileUpload, onBack }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [emailInstructions, setEmailInstructions] = useState('');
  const [selectedModels, setSelectedModels] = useState(['claude', 'openai', 'gemini']);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [enableDataTrace, setEnableDataTrace] = useState(true); // Enable DataTrace by default
  const [useDummyData, setUseDummyData] = useState(false); // Development option

  // Prompt management state
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [promptVersion, setPromptVersion] = useState(1);

  // Second prompt management state
  const [showPrompt2Modal, setShowPrompt2Modal] = useState(false);
  const [currentPrompt2, setCurrentPrompt2] = useState('');
  const [editedPrompt2, setEditedPrompt2] = useState('');
  const [isLoadingPrompt2, setIsLoadingPrompt2] = useState(false);
  const [isSavingPrompt2, setIsSavingPrompt2] = useState(false);
  const [promptVersion2, setPromptVersion2] = useState(1);

  // Prompt selection state (defaults to prompt 1)
  const [selectedPrompt, setSelectedPrompt] = useState('prompt1');

  // Fetch current prompts on mount
  useEffect(() => {
    console.log('=== LMCU REFI UPLOAD COMPONENT LOADED - VERSION 2026-03-03 ===');
    console.log('Edit Prompt feature is enabled with dual prompt support');
    fetchCurrentPrompt();
    fetchCurrentPrompt2();
  }, []);

  const fetchCurrentPrompt = async () => {
    setIsLoadingPrompt(true);
    try {
      const response = await fetch('/api/prompts/current', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPrompt(data.prompt || '');
        setPromptVersion(data.version || 1);
      } else {
        console.error('Failed to fetch prompt');
      }
    } catch (error) {
      console.error('Error fetching prompt:', error);
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const fetchCurrentPrompt2 = async () => {
    setIsLoadingPrompt2(true);
    try {
      const response = await fetch('/api/prompts2/current', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPrompt2(data.prompt || '');
        setPromptVersion2(data.version || 1);
      } else {
        console.error('Failed to fetch prompt2');
      }
    } catch (error) {
      console.error('Error fetching prompt2:', error);
    } finally {
      setIsLoadingPrompt2(false);
    }
  };

  const handleOpenPromptModal = () => {
    setEditedPrompt(currentPrompt);
    setShowPromptModal(true);
  };

  const handleClosePromptModal = () => {
    setShowPromptModal(false);
    setEditedPrompt('');
  };

  const handleOpenPrompt2Modal = () => {
    setEditedPrompt2(currentPrompt2);
    setShowPrompt2Modal(true);
  };

  const handleClosePrompt2Modal = () => {
    setShowPrompt2Modal(false);
    setEditedPrompt2('');
  };

  const handleSavePrompt = async () => {
    if (!editedPrompt.trim()) {
      alert('Prompt cannot be empty');
      return;
    }

    setIsSavingPrompt(true);
    try {
      const response = await fetch('/api/prompts/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ prompt: editedPrompt }),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPrompt(editedPrompt);
        setPromptVersion(data.version || promptVersion + 1);
        setShowPromptModal(false);
        alert('Prompt updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update prompt: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Failed to update prompt. Please try again.');
    } finally {
      setIsSavingPrompt(false);
    }
  };

  const handleSavePrompt2 = async () => {
    if (!editedPrompt2.trim()) {
      alert('Prompt cannot be empty');
      return;
    }

    setIsSavingPrompt2(true);
    try {
      const response = await fetch('/api/prompts2/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ prompt: editedPrompt2 }),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentPrompt2(editedPrompt2);
        setPromptVersion2(data.version || promptVersion2 + 1);
        setShowPrompt2Modal(false);
        alert('Prompt 2 updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update prompt 2: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving prompt2:', error);
      alert('Failed to update prompt 2. Please try again.');
    } finally {
      setIsSavingPrompt2(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file');
      }
    }
  };

  const handleModelToggle = (modelId) => {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) {
          alert('At least one AI model must be selected');
          return prev;
        }
        return prev.filter(id => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  };

  const handleAnalyze = async () => {
    if (!uploadedFile) return;

    setIsAnalyzing(true);
    try {
      // Pass file, selected models, instructions, DataTrace setting, and selected prompt to parent
      await onFileUpload(uploadedFile, {
        models: selectedModels,
        instructions: emailInstructions,
        enableDataTrace: enableDataTrace,
        selectedPrompt: selectedPrompt  // Pass which prompt is currently selected
      });
    } catch (error) {
      console.error('Analysis failed:', error);
      alert('Failed to analyze PDF. Please try again.');
      setIsAnalyzing(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  return (
    <div className="lmcu-upload-container container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Refinance Order</h2>
        <p className="text-muted-foreground">Upload order document for AI-assisted order creation, i.e. AI field extraction and consensus, and data enrichment from SoftPro and 3rd party title vendor APIs.</p>
      </div>

      {/* PDF Upload Section */}
      <Card
        className={`mb-6 transition-all cursor-pointer ${
          isDragging ? 'border-primary border-2 bg-primary/5' : 'border-dashed border-2'
        } ${uploadedFile ? 'bg-muted/30' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <CardContent className="flex flex-col items-center justify-center py-12">
          {uploadedFile ? (
            <>
              <FileText className="h-12 w-12 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-1">{uploadedFile.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">PDF uploaded successfully</p>
              <Button
                type="button"
                variant="destructive"
                onClick={handleRemoveFile}
                disabled={isAnalyzing}
              >
                <X className="mr-2 h-4 w-4" />
                Remove File
              </Button>
            </>
          ) : (
            <>
              <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Drag and drop your order document here</h3>
              <p className="text-sm text-muted-foreground mb-4">or</p>
              <Button
                type="button"
                onClick={() => document.getElementById('lmcu-file-input').click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                Browse Files
              </Button>
              <input
                id="lmcu-file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
              <p className="text-xs text-muted-foreground mt-4">Accepts PDF files only</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Prompt Buttons and Toggle */}
      <div className="mb-6">
        <div className="flex gap-3 mb-3">
          <Button
            variant="secondary"
            onClick={handleOpenPromptModal}
            disabled={isAnalyzing || isLoadingPrompt}
          >
            {isLoadingPrompt ? 'Loading Prompt 1...' : `Edit AI Prompt 1 (v${promptVersion})`}
          </Button>
          <Button
            variant="secondary"
            onClick={handleOpenPrompt2Modal}
            disabled={isAnalyzing || isLoadingPrompt2}
          >
            {isLoadingPrompt2 ? 'Loading Prompt 2...' : `Edit AI Prompt 2 (v${promptVersion2})`}
          </Button>
        </div>

        {/* Prompt Selection Toggle */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label htmlFor="prompt-selector" className="font-medium">Active Prompt:</Label>
              <div className="flex gap-4">
                <Label className="flex items-center gap-2 cursor-pointer" htmlFor="prompt1-radio">
                  <input
                    type="radio"
                    id="prompt1-radio"
                    name="prompt-selector"
                    value="prompt1"
                    checked={selectedPrompt === 'prompt1'}
                    onChange={() => setSelectedPrompt('prompt1')}
                    disabled={isAnalyzing}
                  />
                  <span>Prompt 1</span>
                </Label>
                <Label className="flex items-center gap-2 cursor-pointer" htmlFor="prompt2-radio">
                  <input
                    type="radio"
                    id="prompt2-radio"
                    name="prompt-selector"
                    value="prompt2"
                    checked={selectedPrompt === 'prompt2'}
                    onChange={() => setSelectedPrompt('prompt2')}
                    disabled={isAnalyzing}
                  />
                  <span>Prompt 2</span>
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email/Special Instructions Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Email Contents / Special Instructions</CardTitle>
          <CardDescription>Copy any email contents or special instructions for this order</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={emailInstructions}
            onChange={(e) => setEmailInstructions(e.target.value)}
            placeholder="Paste email contents or add special instructions here..."
            rows={6}
            disabled={isAnalyzing}
          />
        </CardContent>
      </Card>

      {/* Analyze Button */}
      <div className="mb-6">
        <Button
          className="w-full"
          size="lg"
          onClick={handleAnalyze}
          disabled={!uploadedFile || isAnalyzing}
        >
          {isAnalyzing ? 'Analyzing with AI Models...' : 'Analyze Document'}
        </Button>
      </div>

      {/* Options Section */}
      <Card>
        <CardHeader>
          <CardTitle>Options</CardTitle>
          <CardDescription>Configuration and testing options</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3">
            <Label className="flex items-center gap-2 cursor-pointer text-base font-medium" htmlFor="datatrace">
              <Checkbox
                id="datatrace"
                checked={enableDataTrace}
                onCheckedChange={() => setEnableDataTrace(!enableDataTrace)}
                disabled={isAnalyzing}
              />
              <span>Enable automatic DataTrace analysis</span>
            </Label>
            <Label className="flex items-center gap-2 cursor-pointer text-base font-medium" htmlFor="dummy-data">
              <Checkbox
                id="dummy-data"
                checked={useDummyData}
                onCheckedChange={() => setUseDummyData(!useDummyData)}
                disabled={isAnalyzing}
              />
              <span>Use dummy data (skip AI analysis)</span>
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Prompt Edit Modal */}
      <Dialog open={showPromptModal} onOpenChange={setShowPromptModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">Edit AI Analysis Prompt 1</DialogTitle>
            <DialogDescription>
              This prompt is used by all AI models to extract information from uploaded documents.
              Changes will apply to all future document analyses when Prompt 1 is selected.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={editedPrompt}
            onChange={(e) => setEditedPrompt(e.target.value)}
            disabled={isSavingPrompt}
            className="flex-1 min-h-[400px] font-mono text-sm resize-y mb-6"
          />
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleClosePromptModal}
              disabled={isSavingPrompt}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePrompt}
              disabled={isSavingPrompt}
              className="bg-green-500 hover:bg-green-600"
            >
              {isSavingPrompt ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prompt 2 Edit Modal */}
      <Dialog open={showPrompt2Modal} onOpenChange={setShowPrompt2Modal}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">Edit AI Analysis Prompt 2</DialogTitle>
            <DialogDescription>
              This is the second prompt option. It is used by all AI models when Prompt 2 is selected.
              Changes will apply to all future document analyses when Prompt 2 is active.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={editedPrompt2}
            onChange={(e) => setEditedPrompt2(e.target.value)}
            disabled={isSavingPrompt2}
            className="flex-1 min-h-[400px] font-mono text-sm resize-y mb-6"
          />
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleClosePrompt2Modal}
              disabled={isSavingPrompt2}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePrompt2}
              disabled={isSavingPrompt2}
              className="bg-green-500 hover:bg-green-600"
            >
              {isSavingPrompt2 ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LMCURefiUpload;
